import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';
import { cn } from '@/lib/utils';

const PLACEHOLDER = `{
  "mcpServers": {
    "memory": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-memory"]
    }
  }
}`;

export function JsonPastePanel({
  text,
  error,
  choices,
  preview,
  onTextChange,
  onPick,
}: {
  text: string;
  error: string;
  choices: string[];
  preview: { name: string; detail: string } | null;
  onTextChange: (v: string) => void;
  onPick: (name: string) => void;
}) {
  return (
    <YStack className="gap-2.5">
      <div className="overflow-hidden rounded-lg border border-border-strong bg-background-subtle shadow-sm">
        <XStack className="items-center justify-between border-b border-border bg-surface px-2.5 py-1.5">
          <span className="font-mono text-[10px] tracking-wide text-foreground-muted">
            mcpServers.json
          </span>
          <button
            type="button"
            onClick={() => onTextChange('')}
            className="text-[10px] text-foreground-subtle hover:text-foreground"
          >
            Clear
          </button>
        </XStack>
        <TextArea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          spellCheck={false}
          placeholder={PLACEHOLDER}
          className={cn(
            'min-h-[150px] resize-y rounded-none border-0 bg-transparent font-mono text-xs leading-relaxed',
            'focus-visible:border-0'
          )}
        />
      </div>

      {preview && (
        <XStack className="items-center gap-1.5 text-xs text-success-11">
          <Icons.check className="size-3.5 shrink-0" />
          <span>
            Looks good — detected <b className="font-medium">{preview.name}</b>,{' '}
            {preview.detail}.
          </span>
        </XStack>
      )}

      {choices.length > 0 && (
        <YStack className="gap-1.5">
          <span className="text-xs text-foreground-muted">
            Multiple servers found — pick one:
          </span>
          <XStack wrap className="gap-1.5">
            {choices.map((choice) => (
              <Button
                key={choice}
                variant="secondary"
                size="sm"
                onClick={() => onPick(choice)}
              >
                {choice}
              </Button>
            ))}
          </XStack>
        </YStack>
      )}

      {error && <p className="text-xs text-danger-11">{error}</p>}
    </YStack>
  );
}
